import { localeStringComparer } from '@sembo-private/comparisons';
import { captureException } from '@sentry/vue';

import { SemboDate } from '@libs/sembo-dates';
import type {
  ExposedIngredient,
  ExposedRecipe,
  Month,
  OldExposedIngredient,
  OldExposedRecipe,
} from '@libs/trip-planner-interfaces';
import { trackReisegigantenDeserializeOldRecipe } from '@libs/google-analytics-events';
import { AvailabilityOrder } from '@libs/trip-planner-interfaces';
import { exhaustiveGuard } from '@libs/utils';
import type { Serializer } from '@libs/utils';

import { standardDate } from '../../../helpers/formatting';
import type { RecipeMainIngredient } from '../../../recipe/ingredients';
import { isFlightIngredient } from '../../../recipe/ingredients';
import { TripPlannerPax } from '../../../recipe/pax';
import { isReisegiganten } from '../../../helpers/metas';
import {
  isExposedFerryTransportIngredient,
  isExposedFlightTransportIngredient,
  isExposedHotelIngredient,
  isExposedOwnStayIngredient,
  isFerryIngredient,
  isHotelIngredient,
  isOwnStayIngredient,
} from '../../../recipe/ingredients/typeGuards';
import type { TripPlannerQueryString } from '../../propsRouteBuilder/legacy/model';

import {
  createExposedFerryIngredient,
  createFerryIngredient,
} from './recipeSerializer.ferry.helpers';
import {
  createExposedFlightIngredient,
  createFlightIngredient,
} from './recipeSerializer.flight.helpers';
import {
  encodeRecipe,
  decodeRecipe,
  isOldExposedRecipe,
} from './recipeSerializer.helpers';
import {
  createExposedHotelIngredient,
  createHotelIngredient,
} from './recipeSerializer.hotel.helpers';
import {
  createExposedOwnStayIngredient,
  createOwnStayIngredient,
} from './recipeSerializer.ownStay.helpers';

export const RecipeSerializer: Serializer<TripPlannerQueryString> = {
  serialize: function (recipe: TripPlannerQueryString): string {
    const { ingredients, pax, selectedDate, suggestedBy, metadata } = recipe;
    const ingredientsToEncode = ingredients.map((ingredient) => {
      if (isFlightIngredient(ingredient)) {
        return createExposedFlightIngredient(ingredient);
      }

      if (isFerryIngredient(ingredient)) {
        return createExposedFerryIngredient(ingredient);
      }

      if (isHotelIngredient(ingredient)) {
        return createExposedHotelIngredient(ingredient);
      }

      if (isOwnStayIngredient(ingredient)) {
        return createExposedOwnStayIngredient(ingredient);
      }

      return exhaustiveGuard(ingredient, 'Invalid RecipeMainIngredient');
    });

    const selectedDateToEncode = selectedDate
      ? standardDate(selectedDate)
      : undefined;

    const recipeToEncode: ExposedRecipe = {
      ingredients: ingredientsToEncode,
      pax,
      selectedDate: selectedDateToEncode,
      suggestedBy,
      metadata,
    };

    return encodeRecipe(recipeToEncode);
  },
  deserialize: function (value: string): TripPlannerQueryString {
    const recipeJSON = decodeRecipe(value);

    if (
      recipeJSON.affiliateId &&
      isReisegiganten(recipeJSON.affiliateId) &&
      isOldExposedRecipe(recipeJSON)
    ) {
      trackReisegigantenDeserializeOldRecipe();
    }

    const {
      ingredients: decodedIngredients,
      pax: decodedPax,
      sessionId,
    } = recipeJSON;

    const {
      selectedDate: decodedSelectedDate,
      selectedMonth: decodedSelectedMonth,
      suggestedBy: decodedSuggestionSortType,
      startDateOnly,
      endDateOnly,
      metadata,
      searchForPackageRates,
    } = recipeJSON as ExposedRecipe;

    // Backwards Compatibility
    const { suggestionSortType: oldDecodedSuggestionSortType } =
      recipeJSON as OldExposedRecipe;

    let mappedDecodedIngredients: RecipeMainIngredient[] = [];

    try {
      mappedDecodedIngredients = decodedIngredients.map(
        (ingredient: ExposedIngredient | OldExposedIngredient) => {
          if (isExposedHotelIngredient(ingredient)) {
            return createHotelIngredient(ingredient);
          }

          if (isExposedOwnStayIngredient(ingredient)) {
            return createOwnStayIngredient(ingredient);
          }

          if (isExposedFlightTransportIngredient(ingredient)) {
            return createFlightIngredient(ingredient);
          }

          if (isExposedFerryTransportIngredient(ingredient)) {
            return createFerryIngredient(ingredient);
          }

          return exhaustiveGuard(ingredient, 'Invalid ExposedIngredient');
        }
      );
    } catch (error) {
      captureException(error);
    }

    // NOTE: Temporarily while we investigate the issue with extended logging, if any ingredient can not be decoded we return no ingredients at all
    const ingredients: RecipeMainIngredient[] =
      mappedDecodedIngredients.length === decodedIngredients.length
        ? mappedDecodedIngredients
        : [];

    // Backwards Compatibility
    const pax = TripPlannerPax.isOldPax(decodedPax)
      ? TripPlannerPax.getPaxFromOldPax(decodedPax)
      : decodedPax;

    const selectedDate = decodedSelectedDate
      ? new SemboDate(decodedSelectedDate).get()
      : undefined;

    const selectedMonth: Month | undefined =
      startDateOnly && endDateOnly
        ? ((new Date(startDateOnly).getMonth() + 1) as Month)
        : decodedSelectedMonth;

    const suggestedSortType =
      decodedSuggestionSortType ?? oldDecodedSuggestionSortType;

    const suggestedBy = !suggestedSortType
      ? undefined
      : localeStringComparer(suggestedSortType, 'Cheapest', {
          sensitivity: 'accent',
        }) === 0
      ? AvailabilityOrder.Cheapest
      : AvailabilityOrder.Popular;

    return {
      ingredients,
      pax,
      selectedDate,
      selectedMonth,
      suggestedBy,
      sessionId,
      metadata,
      searchForPackageRates,
    };
  },
};
